import * as styles from "../../styles";
import {
  FILTER_SELECT_COLUMN,
  FILTERS_ACTIVE_LIST_MAX_HEIGHT_PX,
  FILTERS_POPOVER_MAX_HEIGHT_PX,
  FILTERS_POPOVER_MAX_WIDTH,
  SUBMENU_INDICATOR,
} from "../../types/constants";
import { chipsByFilterKind } from "../../utils/filterKindUtils";
import { FilterChip } from "../FilterChip";
import SearchableList from "../SearchableList";
import { FilterKindMenu } from "./FilterKindMenu";
import {
  Add as AddIcon,
  FilterList as FilterListIcon,
} from "@mui/icons-material";
import {
  Badge,
  Box,
  Button as MuiButton,
  Divider,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { reportsV3FilterPanelWidthPx } from "config";
import * as labels from "config.labels";
import React from "react";
import { useRef, useState } from "react";
import * as staticDataHooks from "store/hooks/staticDataHooks";

export const FiltersButton = ({
  totalActiveFilterCount,
  activeFilterGroups,
  groupingActive = false,
  columnOptions,
  disabled = false,
  container,
  sliderDraggingRef,
  onSelectColumn,
  onFilterGroupClick,
  onClose,
  renderFilterPanel,
  selectedColumn = "",
  open = false,
  anchorOverride = null,
  onToggle,
  iconOnly = false,
}) => {
  const translateLabels = staticDataHooks.useTranslator("uiLabels");
  const filtersText = translateLabels(labels.FILTERS_BUTTON_FILTERS, {
    default: "Filters",
  });
  const filtersTitle = `${filtersText}${
    totalActiveFilterCount > 0 ? ` (${totalActiveFilterCount})` : ""
  }`;
  const buttonRef = useRef(null);
  const [kindMenu, setKindMenu] = useState(null);
  const handleColumnItemClick = (item, event) => {
    const kinds = item.filterKinds ?? [];
    if (kinds.length > 1) {
      setKindMenu({ column: item.id, kinds, anchorEl: event?.currentTarget });
      return;
    }
    onSelectColumn(item.id, kinds[0]);
  };
  const handleKindSelect = (kind) => {
    const column = kindMenu?.column;
    setKindMenu(null);
    onSelectColumn(column, kind);
  };
  const handleToggle = () => {
    onToggle?.();
  };
  const handleClose = () => {
    onClose?.();
  };
  const handlePopoverClose = (event) => {
    if (showingFilterPanel) {
      if (sliderDraggingRef?.current) return;

      const target = event?.target;
      if (target?.closest?.(".MuiSlider-root")) return;
    }
    handleClose();
  };
  const handleAddFilter = () => {
    onSelectColumn(FILTER_SELECT_COLUMN);
  };
  const showingActiveFilters = !selectedColumn;
  const showingColumnSelector = selectedColumn === FILTER_SELECT_COLUMN;
  const showingFilterPanel =
    selectedColumn && selectedColumn !== FILTER_SELECT_COLUMN;
  return (
    <React.Fragment>
      {iconOnly ? (
        <Tooltip title={filtersTitle}>
          <span>
            <Badge
              color={"primary"}
              badgeContent={totalActiveFilterCount}
              overlap={"circular"}
            >
              <IconButton
                ref={buttonRef}
                size={"small"}
                onClick={handleToggle}
                disabled={disabled}
                sx={{
                  backgroundColor: "grey.100",
                  color: "text.primary",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  "&:hover": { backgroundColor: "grey.200" },
                }}
              >
                <FilterListIcon fontSize={"small"} />
              </IconButton>
            </Badge>
          </span>
        </Tooltip>
      ) : (
        <MuiButton
          variant={"text"}
          size={"small"}
          onClick={handleToggle}
          ref={buttonRef}
          disabled={disabled}
          endIcon={<FilterListIcon fontSize={"small"} />}
          sx={{
            ...styles.buttonText,
            backgroundColor: "grey.100",
            color: "text.primary",
            border: "1px solid",
            borderColor: "divider",
            "&:hover": {
              backgroundColor: "grey.200",
            },
            padding: "4px 8px",
          }}
        >
          {filtersTitle}
        </MuiButton>
      )}
      <Popover
        open={open}
        anchorEl={anchorOverride ?? buttonRef.current}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        container={container ?? undefined}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx: {
              width: showingFilterPanel
                ? reportsV3FilterPanelWidthPx()
                : "max-content",
              maxWidth: showingFilterPanel
                ? undefined
                : FILTERS_POPOVER_MAX_WIDTH,
              maxHeight: FILTERS_POPOVER_MAX_HEIGHT_PX,
              display: "flex",
              flexDirection: "column",
              overflow: showingFilterPanel ? "hidden" : undefined,
            },
          },
        }}
      >
        {showingActiveFilters && (
          <React.Fragment>
            <Box
              sx={{
                px: 2,
                pt: 2,
                pb: 1,
              }}
            >
              <Typography
                variant={"subtitle2"}
                sx={{
                  fontWeight: 600,
                }}
              >
                {translateLabels(labels.FILTERS_BUTTON_ACTIVE_FILTERS, {
                  default: "Active filters",
                })}
              </Typography>
            </Box>
            {activeFilterGroups.length === 0 ? (
              <Box
                sx={{
                  px: 2,
                  pb: 2,
                }}
              >
                <Typography
                  variant={"body2"}
                  color={"text.secondary"}
                  sx={{
                    mt: 0.5,
                  }}
                >
                  {translateLabels(labels.FILTERS_BUTTON_NO_ACTIVE_FILTERS, {
                    default: "No active filters yet.",
                  })}
                </Typography>
              </Box>
            ) : (
              <Stack
                spacing={1}
                sx={{
                  maxHeight: FILTERS_ACTIVE_LIST_MAX_HEIGHT_PX,
                  overflowY: "auto",
                  px: 2,
                  pb: 1,
                }}
                alignItems={"flex-start"}
              >
                {activeFilterGroups.map((group) => (
                  <Box key={group.column}>
                    <Typography
                      variant={"caption"}
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        px: 1,
                      }}
                    >
                      {group.columnLabel}
                    </Typography>
                    {group.sections.map((section) => (
                      <Box
                        key={section.kind}
                        onClick={() =>
                          onFilterGroupClick(group.column, section.kind)
                        }
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "nowrap",
                          whiteSpace: "nowrap",
                          gap: 0.5,
                          borderRadius: 9999,
                          backgroundColor: "rgba(0, 0, 0, 0)",
                          border: "1px solid",
                          borderColor: "rgba(0, 0, 0, 0)",
                          px: 1,
                          py: 0.5,
                          width: "fit-content",
                          cursor: "pointer",
                          transition:
                            "background-color 0.2s, border-color 0.2s",
                          "&:hover": {
                            backgroundColor: "action.selected",
                            borderColor: "action.selected",
                          },
                        }}
                      >
                        <Typography
                          variant={"caption"}
                          sx={{ color: "text.secondary" }}
                        >
                          {section.kindLabel}
                        </Typography>
                        {section.chips.map((chip) => (
                          <FilterChip
                            key={chip.key}
                            label={chip.label}
                            tooltip={chip.tooltip}
                            onDelete={chip.onDelete}
                          />
                        ))}
                      </Box>
                    ))}
                  </Box>
                ))}
              </Stack>
            )}
            <Divider />
            <Box
              sx={{
                px: 2,
                py: 2,
              }}
            >
              <MuiButton
                variant={"outlined"}
                size={"small"}
                fullWidth
                startIcon={<AddIcon />}
                onClick={handleAddFilter}
                sx={{
                  textTransform: "none",
                  color: "text.primary",
                  borderColor: "divider",
                }}
              >
                {translateLabels(labels.FILTERS_BUTTON_ADD_FILTER, {
                  default: "Add filter",
                })}
              </MuiButton>
            </Box>
          </React.Fragment>
        )}
        {showingColumnSelector && (
          <SearchableList
            items={columnOptions.map((col) => ({
              id: col.column,
              label: col.displayName,
              filterKinds: col.filterKinds,
              endIcon:
                col.hasFilter || col.filterKinds?.length > 1 ? (
                  <React.Fragment>
                    {col.hasFilter && (
                      <FilterListIcon fontSize={"small"} color={"primary"} />
                    )}
                    {col.filterKinds?.length > 1 && SUBMENU_INDICATOR}
                  </React.Fragment>
                ) : undefined,
            }))}
            onItemClick={handleColumnItemClick}
            isOpen={open && showingColumnSelector}
            searchPlaceholder={translateLabels(
              labels.FILTERS_BUTTON_SEARCH_PLACEHOLDER,
              { default: "columns" }
            )}
            header={{
              title: translateLabels(labels.FILTERS_BUTTON_SELECT_COLUMN, {
                default: "Select column",
              }),
              onBack: handleClose,
            }}
            emptyMessage={translateLabels(labels.FILTERS_BUTTON_EMPTY_MESSAGE, {
              default: "No columns found",
            })}
          />
        )}
        <FilterKindMenu
          anchorEl={kindMenu?.anchorEl ?? null}
          open={Boolean(kindMenu)}
          kinds={kindMenu?.kinds ?? []}
          chipsByKind={chipsByFilterKind(activeFilterGroups, kindMenu?.column)}
          groupingActive={groupingActive}
          onSelect={handleKindSelect}
          onClose={() => setKindMenu(null)}
          container={container}
        />
        {showingFilterPanel &&
          renderFilterPanel?.({
            open,
            onClose: handleClose,
          })}
      </Popover>
    </React.Fragment>
  );
};
